import { EventHookManager, IEventArgsBase, IProperyChangedArgs } from "../Utilities";
import { BaseViewModel, IEntityConstructorOptions } from "./Base/BaseViewModel";
import { IManageEntities } from "./Services/ItemsManager";
import { ICorrelation } from "./Utilities";
import { MemoryCache } from "../Services/DataService/MemoryCache";
import { DataServices } from "../Services/DataService/DataService";
import { BaseProperties } from "../Services/DataService/Model/Base/DataEntityBase";
import { EntityManagerRegistrar } from "./Services/EntityManagerRegistrar";
import { EntityKey, EntityId } from "../Services/DataService/Model/Base/Models";
import { Mutex } from "../Utilities/Mutext";


export enum CacheModelChangedType {
    Added = "Added",
    // Updated = "Updated",
    Deleted = "Deleted"
}

export interface ICacheModelChangedArgs<T extends BaseViewModel> extends IEventArgsBase {
    type: CacheModelChangedType;
    item: T;
}


export class ModelManager<T extends BaseViewModel> implements IManageEntities<T> {

    constructor({ createFN, idFromKeyFN, typeName }:
        {
            createFN: (options?: IEntityConstructorOptions) => Promise<T>,
            idFromKeyFN: (key: string) => string,
            typeName: string,
            onEntitiesChanged?: (params: ICacheModelChangedArgs<T>) => void
        }) {
        this._createFN = createFN;
        this._idFromKeyFN = idFromKeyFN;
        this._typeName = typeName;

        this._memoryCache = new MemoryCache(
            {
                doReadFN: async (key: string) => {
                    var obj = await DataServices.Raw.readData(key); //todo: determine if obj can be undefined
                    if (!obj)
                        return null;
                    return this._createFN({ sourceObject: obj });
                },
                doDeleteFN: async (id: string) => {
                    await DataServices.Raw.deleteData(id);
                },
                debugName: `ModelManager|${typeName}`,
                skipCache: false
            }
        );


        EntityManagerRegistrar.RegisterManager(typeName, this);
    }

    public get EntitiesChanged() {
        return this._entitiesChangedHook.eventHook;
    }

    public get EntityPropertyChanged() {
        return this._entityPropertyChanged.eventHook;
    }

    public ToggleEvents = () => this._shouldFireEvents = !this._shouldFireEvents;



    CreateItem = async (_correlation: ICorrelation, options?: IEntityConstructorOptions, bypassGuard = false): Promise<T> => {

        let eventPayload: any | undefined = undefined;
        const CreateItem_FN = async () => {

            var id = options?.sourceObject?.id ?? options?.dataEntity?.id;
            if (await this.GetItemById(_correlation, id))
                throw new Error(`ModelManager | Item already exists with id: ${id}`);

            try {
                console.log(`ModelManager | Creating item with id: ${id}`);
                var retVal = await this._createFN(options);
                await this._memoryCache.WriteData(retVal);
                await DataServices.Raw.writeData(retVal.Data!);
                eventPayload = {
                    source: this,
                    type: CacheModelChangedType.Added,
                    item: retVal
                };
                retVal.PropertyChanged.Subscribe(this.forwardChildPropertyChanged);
                return retVal;
            } catch(e) {
                throw e;
            }
        };

        let result: T | null = null;
        if (bypassGuard)
            result = await CreateItem_FN();
        else
            result = (await this.instantiationMutext.lock(CreateItem_FN, { operation: "CreateItem", correlation: _correlation })) as T;

        if (eventPayload && this._shouldFireEvents)
            this._entitiesChangedHook.Fire(eventPayload);

        return result;
    }




    GetItemByKey = async (_correlation: ICorrelation, key: EntityKey, bypassGuard = false) => {

        const GetItemByKey_FN = async () => {
            if (!key)
                return null;
            const id = this._idFromKeyFN(key);
            var item = await this.GetItemById(_correlation, id);
            item?.PropertyChanged.Subscribe(this.forwardChildPropertyChanged);
            return item;
        }

        if (bypassGuard)
            return await GetItemByKey_FN();
        else
            return await this.instantiationMutext.lock(GetItemByKey_FN);
    }

    GetOrCreateByKey = async (_correlation: ICorrelation, key: EntityKey, createOptions?: IEntityConstructorOptions): Promise<T> => {


        return await this.instantiationMutext.lock(

            async () => {
                var item = await this.GetItemByKey(_correlation, key, true);
                if (!item) {
                    item = await this.CreateItem(_correlation, { ...createOptions }, true);
                    await DataServices.Raw.writeData(item.Data!);
                }
                return item;
            },
            {
                operation: `GetOrCreateByKey | ${key}`,
                correlation: _correlation
            }

        ) as T;
    }

    private instantiationMutext = new Mutex("ModelManager.instantiationMutext");



    GetItemById = async (_correlation: ICorrelation, id: EntityId) => {
        if (!id)
            return null;
        var item = await this._memoryCache.ReadData(id) as T;
        item?.PropertyChanged.Subscribe(this.forwardChildPropertyChanged);
        return item;
    }




    GetAllItems = async (_correlation: ICorrelation, _fromCache?: boolean) => {

        return await this.instantiationMutext.lock(
            async () => {
                //
                // Setup Varuabkes
                //
                var items = await DataServices.Raw.readAllByType(this._typeName);
                var retVal: T[] = [];

                //
                // Process the items
                //
                for (let item of items) {
                    //
                    // if we have a cached item, just use it, otherwise hydrate a new one and cache it.
                    //
                    if (this._memoryCache.HasCachedItem(_correlation, item[BaseProperties.id]))
                        retVal.push((await this._memoryCache.ReadData(item[BaseProperties.id])) as T);
                    else {
                        var vm = await this._createFN({ sourceObject: item });
                        retVal.push(vm);
                        await this._memoryCache.WriteData(vm);
                    }
                    retVal[retVal.length - 1].PropertyChanged.Subscribe(this.forwardChildPropertyChanged);
                }
                return retVal;
            }
        ) as T[];
    };


    public GetAllIds = () => this._memoryCache.GetAllIds();


    DeleteItem = async (_correlation: ICorrelation, _item: T) => {

        if (!_item.Data?.id)
            throw new Error("Item does not have an id");

        _item?.PropertyChanged.Unsubscribe(this.forwardChildPropertyChanged);

        await this._memoryCache.DeleteData(_item.Data.id);

        this._shouldFireEvents && this._entitiesChangedHook.Fire(
            {
                source: this,
                type: CacheModelChangedType.Deleted,
                item: _item
            }
        );
    }




    private forwardChildPropertyChanged = (args: IProperyChangedArgs) => {
        this._shouldFireEvents && this._entityPropertyChanged.Fire(args);
    };


    private _createFN: (options?: IEntityConstructorOptions) => Promise<T>;
    private _idFromKeyFN: (key: string) => string;
    private _typeName: string;

    private _entityPropertyChanged = new EventHookManager<IProperyChangedArgs>();
    private _entitiesChangedHook = new EventHookManager<ICacheModelChangedArgs<T>>();
    private _memoryCache: MemoryCache;
    private _shouldFireEvents = true;
}